import React from 'react';
import { PlusIcon, PencilIcon, ArchiveBoxIcon, ArrowPathIcon, XMarkIcon } from '@heroicons/react/24/outline';
import PageShell from '../../components/PageShell';
import { useCustomVariablesPage } from './useCustomVariablesPage';

const TYPE_LABELS: Record<string, string> = { text: 'Text', dropdown: 'List', boolean: 'Boolean' };

const BOOLEAN_PRESETS = [
  { label: 'Yes / No', values: ['Yes', 'No'] },
  { label: 'True / False', values: ['True', 'False'] },
  { label: 'On / Off', values: ['On', 'Off'] },
  { label: 'Active / Inactive', values: ['Active', 'Inactive'] },
  { label: 'Custom', values: [] },
];

const CustomVariablesPage: React.FC = () => {
  const h = useCustomVariablesPage();

  if (h.eligible === false) {
    return (
      <PageShell className="max-w-4xl mx-auto p-4 sm:p-6">
        <div className="bg-gradient-to-br from-slate-800/60 to-slate-900/80 rounded-xl border border-white/10 p-8 text-center">
          <h2 className="text-xl font-semibold text-white mb-2">Custom Fields</h2>
          <p className="text-gray-400 mb-4">Custom fields require a Growth or Professional subscription.</p>
          <a href="/subscription" className="inline-flex items-center px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg text-sm font-medium transition-colors">
            Upgrade Plan
          </a>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell className="max-w-4xl mx-auto p-4 sm:p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-white">Custom Fields</h1>
          <p className="text-sm text-gray-400 mt-1">Create fields to enrich your agent prompts. Attach up to 3 per agent.</p>
        </div>
        <button
          onClick={() => h.setShowCreateModal(true)}
          className="flex items-center gap-1.5 px-3 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg text-sm font-medium transition-colors"
        >
          <PlusIcon className="h-4 w-4" />
          New Field
        </button>
      </div>

      {h.error && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-300 text-sm">{h.error}</div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-3 mb-4">
        <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
          <input
            type="checkbox"
            checked={h.showArchived}
            onChange={e => h.setShowArchived(e.target.checked)}
            className="rounded border-gray-600 bg-gray-800 text-cyan-500 focus:ring-cyan-500"
          />
          Show archived
        </label>
        <span className="text-xs text-gray-400">{h.variables.length} field{h.variables.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Variables List */}
      {h.loading ? (
        <div className="text-center py-12 text-gray-400">Loading...</div>
      ) : h.variables.length === 0 ? (
        <div className="bg-gradient-to-br from-slate-800/40 to-slate-900/60 rounded-xl border border-white/10 p-8 text-center">
          <p className="text-gray-400">No custom fields yet. Create one to get started.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {h.variables.map(v => (
            <div
              key={v.id}
              className={`bg-gradient-to-br from-slate-800/40 to-slate-900/60 rounded-lg border border-white/10 p-4 flex items-center justify-between ${v.is_archived ? 'opacity-60' : ''}`}
            >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <span className="text-white font-medium truncate">{v.name}</span>
                <span className="text-xs text-gray-400">{TYPE_LABELS[v.variable_type]}</span>
                {v.variable_type === 'dropdown' && v.dropdown_options && (
                  <span className="text-xs text-gray-400">{v.dropdown_options.length} options</span>
                )}
                {v.variable_type === 'boolean' && v.dropdown_options && v.dropdown_options.length === 2 && (
                  <span className="text-xs text-gray-400">{v.dropdown_options[0]} / {v.dropdown_options[1]}</span>
                )}
                {v.is_archived && <span className="text-xs text-amber-400/60">Archived</span>}
              </div>
              <div className="flex items-center gap-1">
                {!v.is_archived && (
                  <>
                    <button onClick={() => h.openEdit(v)} className="p-1.5 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-white/5" title="Edit">
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button onClick={() => h.handleArchive(v.id)} className="p-1.5 text-gray-400 hover:text-amber-400 transition-colors rounded-lg hover:bg-white/5" title="Archive">
                      <ArchiveBoxIcon className="h-4 w-4" />
                    </button>
                  </>
                )}
                {v.is_archived && (
                  <button onClick={() => h.handleRestore(v.id)} className="p-1.5 text-gray-400 hover:text-green-400 transition-colors rounded-lg hover:bg-white/5" title="Restore">
                    <ArrowPathIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {h.showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-gray-900 border border-white/10 rounded-xl w-full max-w-md p-6 mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">New Field</h3>
              <button onClick={() => h.setShowCreateModal(false)} className="text-gray-400 hover:text-white"><XMarkIcon className="h-5 w-5" /></button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Name</label>
                <input
                  value={h.createName}
                  onChange={e => h.setCreateName(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-cyan-500"
                  placeholder="e.g. Property Type"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Type</label>
                <select
                  value={h.createType}
                  onChange={e => h.setCreateType(e.target.value as any)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-cyan-500"
                >
                  <option value="text">Text</option>
                  <option value="dropdown">List</option>
                  <option value="boolean">Boolean</option>
                </select>
              </div>

              {h.createType === 'dropdown' && (
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Options</label>
                  {h.createDropdownOptions.map((opt, i) => (
                    <div key={i} className="flex items-center gap-2 mb-1">
                      <input
                        value={opt}
                        onChange={e => {
                          const newOpts = [...h.createDropdownOptions];
                          newOpts[i] = e.target.value;
                          h.setCreateDropdownOptions(newOpts);
                        }}
                        className="flex-1 px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-cyan-500"
                        placeholder={`Option ${i + 1}`}
                      />
                      {h.createDropdownOptions.length > 1 && (
                        <button
                          onClick={() => h.setCreateDropdownOptions(h.createDropdownOptions.filter((_, j) => j !== i))}
                          className="text-gray-400 hover:text-red-400"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    onClick={() => h.setCreateDropdownOptions([...h.createDropdownOptions, ''])}
                    className="text-xs text-cyan-400 hover:text-cyan-300 mt-1"
                  >
                    + Add option
                  </button>
                </div>
              )}

              {h.createType === 'boolean' && (
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Format</label>
                  <div className="space-y-1.5">
                    {BOOLEAN_PRESETS.map((preset, i) => (
                      <label
                        key={i}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                          h.createBooleanPreset === i
                            ? 'border-cyan-500/50 bg-cyan-500/10'
                            : 'border-gray-700 hover:border-gray-600'
                        }`}
                      >
                        <input
                          type="radio"
                          name="booleanPreset"
                          checked={h.createBooleanPreset === i}
                          onChange={() => h.handleBooleanPresetChange(i)}
                          className="text-cyan-500 focus:ring-cyan-500 bg-gray-800 border-gray-600"
                        />
                        <span className="text-sm text-white">{preset.label}</span>
                      </label>
                    ))}
                  </div>
                  {h.createBooleanPreset === BOOLEAN_PRESETS.length - 1 && (
                    <div className="flex items-center gap-2 mt-2">
                      <input
                        value={h.createBooleanCustomValues[0]}
                        onChange={e => h.setCreateBooleanCustomValues([e.target.value, h.createBooleanCustomValues[1]])}
                        className="flex-1 px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-cyan-500"
                        placeholder="Value 1 (e.g. East)"
                      />
                      <span className="text-gray-400">/</span>
                      <input
                        value={h.createBooleanCustomValues[1]}
                        onChange={e => h.setCreateBooleanCustomValues([h.createBooleanCustomValues[0], e.target.value])}
                        className="flex-1 px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-cyan-500"
                        placeholder="Value 2 (e.g. West)"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            <p className="text-xs text-gray-400 mt-3">Custom fields are configured per agent. You can attach up to 3 fields to each agent.</p>

            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => h.setShowCreateModal(false)} className="px-3 py-2 text-sm text-gray-400 hover:text-white transition-colors">Cancel</button>
              <button
                onClick={h.handleCreate}
                disabled={h.creating || !h.createName.trim()}
                className="px-4 py-2 bg-cyan-500 hover:bg-cyan-600 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
              >
                {h.creating ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {h.editVariable && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-gray-900 border border-white/10 rounded-xl w-full max-w-md p-6 mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Edit Field</h3>
              <button onClick={() => h.setEditVariable(null)} className="text-gray-400 hover:text-white"><XMarkIcon className="h-5 w-5" /></button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Name</label>
                <input
                  value={h.editName}
                  onChange={e => h.setEditName(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-cyan-500"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Type</label>
                <div className="px-3 py-2 bg-gray-800/50 border border-gray-700/50 rounded-lg text-gray-400 text-sm">
                  {TYPE_LABELS[h.editVariable.variable_type]} <span className="text-xs">(cannot change type)</span>
                </div>
              </div>

              {h.editVariable.variable_type === 'dropdown' && (
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Options</label>
                  {h.editDropdownOptions.map((opt, i) => (
                    <div key={i} className="flex items-center gap-2 mb-1">
                      <input
                        value={opt}
                        onChange={e => {
                          const newOpts = [...h.editDropdownOptions];
                          newOpts[i] = e.target.value;
                          h.setEditDropdownOptions(newOpts);
                        }}
                        className="flex-1 px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-cyan-500"
                      />
                      {h.editDropdownOptions.length > 1 && (
                        <button
                          onClick={() => h.setEditDropdownOptions(h.editDropdownOptions.filter((_, j) => j !== i))}
                          className="text-gray-400 hover:text-red-400"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    onClick={() => h.setEditDropdownOptions([...h.editDropdownOptions, ''])}
                    className="text-xs text-cyan-400 hover:text-cyan-300 mt-1"
                  >
                    + Add option
                  </button>
                </div>
              )}

              {h.editVariable.variable_type === 'boolean' && (
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Values</label>
                  <div className="flex items-center gap-2">
                    <input
                      value={h.editDropdownOptions[0] || ''}
                      onChange={e => {
                        const newOpts = [...h.editDropdownOptions];
                        newOpts[0] = e.target.value;
                        h.setEditDropdownOptions(newOpts);
                      }}
                      className="flex-1 px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-cyan-500"
                      placeholder="Value 1"
                    />
                    <span className="text-gray-400">/</span>
                    <input
                      value={h.editDropdownOptions[1] || ''}
                      onChange={e => {
                        const newOpts = [...h.editDropdownOptions];
                        newOpts[1] = e.target.value;
                        h.setEditDropdownOptions(newOpts);
                      }}
                      className="flex-1 px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-cyan-500"
                      placeholder="Value 2"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => h.setEditVariable(null)} className="px-3 py-2 text-sm text-gray-400 hover:text-white transition-colors">Cancel</button>
              <button
                onClick={h.handleSaveEdit}
                disabled={h.saving || !h.editName.trim()}
                className="px-4 py-2 bg-cyan-500 hover:bg-cyan-600 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
              >
                {h.saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
};

export default CustomVariablesPage;
